import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Trash2, Edit2, DollarSign, Users, Eye, EyeOff, Hash } from 'lucide-react';

export default function CustomerList({ user }) {
  const [customers, setCustomers] = useState([]);
  const [incomeSources, setIncomeSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [hideAmounts, setHideAmounts] = useState(() => {
    const saved = localStorage.getItem('hideCustomerAmounts');
    return saved !== null ? JSON.parse(saved) : true; // Default to hidden
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    source: ''
  });

  // Save hide preference to localStorage
  const toggleHideAmounts = () => {
    const newValue = !hideAmounts;
    setHideAmounts(newValue);
    localStorage.setItem('hideCustomerAmounts', JSON.stringify(newValue));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch customers with their income totals
      const [customersRes, sourcesRes] = await Promise.all([
        axios.get(`${API}/income-categories`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/income-sources`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      // Fetch all income to calculate totals per customer
      const incomeRes = await axios.get(`${API}/income`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Calculate total income and transaction count per customer
      const customerTotals = {};
      const customerCounts = {};
      incomeRes.data.forEach(income => {
        if (income.category) {
          if (!customerTotals[income.category]) {
            customerTotals[income.category] = 0;
            customerCounts[income.category] = 0;
          }
          customerTotals[income.category] += income.amount;
          customerCounts[income.category] += 1;
        }
      });

      // Merge customers with their totals and counts
      const customersWithTotals = customersRes.data.map(customer => ({
        ...customer,
        totalIncome: customerTotals[customer.name] || 0,
        transactionCount: customerCounts[customer.name] || 0
      }));

      // Sort by total income (highest first)
      customersWithTotals.sort((a, b) => b.totalIncome - a.totalIncome);

      setCustomers(customersWithTotals);
      setIncomeSources(sourcesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setEditFormData({
      name: customer.name,
      source: customer.source
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editFormData.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!editFormData.source) {
      toast.error('Revenue category is required');
      return;
    }

    try {
      await axios.put(
        `${API}/income-categories/${editingCustomer.id}`,
        editFormData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      toast.success('Customer updated successfully');
      setShowEditDialog(false);
      setEditingCustomer(null);
      fetchData();
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    }
  };

  const handleDelete = async (customer) => {
    if (customer.totalIncome > 0) {
      toast.error(`Cannot delete "${customer.name}" - this customer has income entries. Delete those first.`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${customer.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/income-categories/${customer.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Customer deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-slate-600">Loading customers...</div>
      </div>
    );
  }

  const totalRevenue = customers.reduce((sum, customer) => sum + customer.totalIncome, 0);

  return (
    <div className="p-3 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 md:w-8 md:h-8 shrink-0" />
            Customer List
          </h1>
          <p className="text-xs md:text-sm text-slate-600 mt-1">
            Manage your customers and view their revenue contributions
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={toggleHideAmounts}
          className="flex items-center gap-1 shrink-0 text-xs md:text-sm"
          data-testid="toggle-amounts-btn"
        >
          {hideAmounts ? (
            <>
              <EyeOff className="w-4 h-4" />
              <span className="hidden sm:inline">Show</span>
            </>
          ) : (
            <>
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline">Hide</span>
            </>
          )}
        </Button>
      </div>

      {/* Summary Cards - 2x2 grid on mobile */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
        <Card>
          <CardHeader className="p-3 md:pb-2 md:p-6">
            <CardDescription className="text-xs md:text-sm">Total Customers</CardDescription>
            <CardTitle className="text-xl md:text-3xl">{customers.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="p-3 md:pb-2 md:p-6">
            <CardDescription className="text-xs md:text-sm">Total Revenue</CardDescription>
            <CardTitle className="text-lg md:text-3xl text-green-600 truncate">
              {hideAmounts ? '••••••' : formatCurrency(totalRevenue)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="p-3 md:pb-2 md:p-6">
            <CardDescription className="text-xs md:text-sm">Active Customers</CardDescription>
            <CardTitle className="text-xl md:text-3xl">{customers.filter(c => c.totalIncome > 0).length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="p-3 md:pb-2 md:p-6">
            <CardDescription className="text-xs md:text-sm">Total Transactions</CardDescription>
            <CardTitle className="text-xl md:text-3xl">{customers.reduce((sum, c) => sum + c.transactionCount, 0)}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Customer List */}
      <Card>
        <CardHeader className="p-3 md:p-6">
          <CardTitle className="text-base md:text-lg">All Customers</CardTitle>
          <CardDescription className="text-xs md:text-sm">
            Edit customer details or delete customers with no income history
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3 md:p-6 pt-0 md:pt-0">
          {customers.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg">No customers yet</p>
              <p className="text-sm">Add customers from the Home page when creating income entries</p>
            </div>
          ) : (
            <>
              {/* Mobile: Card layout */}
              <div className="md:hidden space-y-3" data-testid="customer-list-mobile">
                {customers.map((customer) => (
                  <div key={customer.id} className="border border-slate-700 rounded-lg p-3 space-y-2" data-testid={`customer-card-${customer.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm">{customer.name}</span>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleEdit(customer)} data-testid={`edit-customer-${customer.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className={`h-7 w-7 p-0 ${customer.totalIncome > 0 ? 'opacity-50 cursor-not-allowed' : 'text-red-600 hover:text-red-700'}`}
                          onClick={() => handleDelete(customer)}
                          disabled={customer.totalIncome > 0}
                          data-testid={`delete-customer-${customer.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">
                        {customer.source}
                      </span>
                      <span className="text-slate-500">{customer.transactionCount} txn{customer.transactionCount !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="text-right">
                      <span className={`font-semibold text-sm ${customer.totalIncome > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                        {hideAmounts ? '••••••' : formatCurrency(customer.totalIncome)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Table layout */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30%]">Customer Name</TableHead>
                      <TableHead className="w-[25%]">Revenue Category</TableHead>
                      <TableHead className="text-center w-[10%]">Transactions</TableHead>
                      <TableHead className="text-right w-[15%]">Total Income</TableHead>
                      <TableHead className="text-center w-[20%]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customer.id} className="hover:bg-slate-50">
                        <TableCell className="font-semibold">{customer.name}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {customer.source}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="inline-flex items-center gap-1 text-sm text-slate-600">
                            <Hash className="w-4 h-4" />
                            <span>{customer.transactionCount}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${customer.totalIncome > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                            {hideAmounts ? '••••••' : formatCurrency(customer.totalIncome)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleEdit(customer)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(customer)}
                              disabled={customer.totalIncome > 0}
                              className={customer.totalIncome > 0 ? 'opacity-50 cursor-not-allowed' : 'text-red-600 hover:text-red-700 hover:bg-red-50'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
            <DialogDescription>
              Update customer name and revenue category
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Customer Name</Label>
              <Input
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                placeholder="Enter customer name"
              />
            </div>
            <div className="space-y-2">
              <Label>Revenue Category</Label>
              <Select
                value={editFormData.source}
                onValueChange={(value) => setEditFormData({ ...editFormData, source: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select revenue category" />
                </SelectTrigger>
                <SelectContent>
                  {incomeSources.map((source) => (
                    <SelectItem key={source.id} value={source.name}>
                      {source.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSaveEdit} className="flex-1">
                Save Changes
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEditDialog(false);
                  setEditingCustomer(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
